import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Container,
  IconButton,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { parseMultiLangContent } from '../../utils/imageHelper';
import { getActiveLanguage } from '../../i18n';
import { API_URL, handleApiError } from '../../utils/config';
import Pagination from '../Common/Pagination';
import ProductFilters from './ProductFilters';
import FilterBottomSheet from './FilterBottomSheet';
import SEO from '../SEO/SEO';
import StyledProductCard from './StyledProductCard';
import { trackViewCategory, trackViewItemList } from '../../utils/marketingAnalytics';
import {
  buildCategoryListTrackingKey,
  shouldTrackCategoryList,
} from '../../utils/productListTracking';
import { COLORS, FONTS } from '../../theme/tokens';
import useFilterOptions, {
  parseFiltersFromUrl,
  buildFilterUrl,
  clearAllFilterParams,
  resolveFilterChips,
} from './useFilterOptions';

const SORT_LABEL_KEYS = {
  default: 'shop.sortDefault',
  price_low_high: 'shop.sortPriceLowHigh',
  price_high_low: 'shop.sortPriceHighLow',
  newest: 'shop.sortNewest',
};

const MOBILE_SORT_LISTBOX_ID = 'mobile-product-sort-options';

const ProductList = ({ attributeProducts = false }) => {
  const { t, i18n } = useTranslation();
  const activeLang = getActiveLanguage();
  void i18n.language;
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 24,
  });
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [sortOption, setSortOption] = useState('');
  const [defaultSortLoaded, setDefaultSortLoaded] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const lastTrackedItemList = useRef(null);

  const { filters: filterOptions } = useFilterOptions();

  const { slug, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const listAnalytics = useMemo(() => {
    const rawTitle = attributeProducts
      ? categoryInfo?.attribute_title || categoryInfo?.menu_title
      : categoryInfo?.title;
    const fallbackName = attributeProducts ? `Attribute ${id}` : slug || 'Products';

    return {
      listId: attributeProducts ? `attribute_${id || 'products'}` : slug || 'products',
      listName: rawTitle ? parseMultiLangContent(rawTitle, activeLang) : fallbackName,
    };
  }, [attributeProducts, categoryInfo, id, slug, activeLang]);

  useEffect(() => {
    setSelectedFilters(parseFiltersFromUrl(location.search));
  }, [location.search]);

  const appliedChips = useMemo(
    () => resolveFilterChips(selectedFilters, filterOptions),
    [selectedFilters, filterOptions]
  );

  const activeFilterCount = useMemo(
    () => Object.values(selectedFilters).reduce((sum, ids) => sum + ids.length, 0),
    [selectedFilters]
  );

  const openFilterSheet = () => {
    setFilterSheetOpen(true);
  };

  const closeFilterSheet = () => {
    setFilterSheetOpen(false);
  };

  const handleApplyFiltersFromSheet = (draftFilters) => {
    navigate(buildFilterUrl(location.pathname, location.search, draftFilters), { replace: true });
    setFilterSheetOpen(false);
  };

  const removeChip = (attributeId, childId) => {
    const newFilters = { ...selectedFilters };
    if (newFilters[attributeId]) {
      newFilters[attributeId] = newFilters[attributeId].filter((cid) => cid !== childId);
      if (newFilters[attributeId].length === 0) {
        delete newFilters[attributeId];
      }
    }
    navigate(buildFilterUrl(location.pathname, location.search, newFilters), { replace: true });
  };

  const handleClearAllChips = () => {
    navigate(clearAllFilterParams(location.pathname, location.search), { replace: true });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sortFromUrl = searchParams.get('sort');

    if (sortFromUrl) {
      setSortOption(sortFromUrl);
      setDefaultSortLoaded(true);
      return;
    }

    if (attributeProducts && id) {
      const fetchAttributeInfo = async () => {
        try {
          const response = await fetch(`${API_URL}/attributes/${id}`);
          if (response.ok) {
            const data = await response.json();
            setCategoryInfo(data);

            if (data.default_sort) {
              setSortOption(data.default_sort);
            }
          }
        } catch (fetchError) {
          console.error('Error fetching attribute info:', fetchError);
        } finally {
          setDefaultSortLoaded(true);
        }
      };
      fetchAttributeInfo();
    } else if (!attributeProducts && slug) {
      const fetchCategoryInfo = async () => {
        try {
          const response = await fetch(`${API_URL}/categories/slug/${slug}`);
          if (response.ok) {
            const responseBody = await response.json();
            const categoryData = responseBody?.success === true && responseBody.data
              ? responseBody.data
              : responseBody;
            setCategoryInfo(categoryData);
          }
        } catch (fetchError) {
          console.error('Error fetching category info:', fetchError);
        } finally {
          setDefaultSortLoaded(true);
        }
      };
      fetchCategoryInfo();
    } else {
      setDefaultSortLoaded(true);
    }
  }, [slug, id, attributeProducts, location.search]);

  useEffect(() => {
    if (!defaultSortLoaded) return;
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page'), 10) || 1;
    const limit = parseInt(searchParams.get('limit'), 10) || 24;

    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      itemsPerPage: limit,
    }));

    if ((attributeProducts && id) || (!attributeProducts && slug)) {
      const cacheKey = attributeProducts ? `attribute_products_${id}` : `category_products_${slug}`;
      sessionStorage.removeItem(cacheKey);

      const fetchData = async () => {
        try {
          setLoading(true);
          setError(null);

          const filterParams = [];
          for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
              filterParams.push(`${key}=${value}`);
            }
          }

          let endpoint;
          if (attributeProducts && id) {
            endpoint = `${API_URL}/attributes/${id}/products?page=${page}&limit=${limit}`;
          } else {
            endpoint = `${API_URL}/products/category/${slug}?page=${page}&limit=${limit}`;
          }

          if (filterParams.length > 0) {
            endpoint += `&${filterParams.join('&')}`;
          }

          if (sortOption) {
            endpoint += `&sort=${sortOption}`;
          }

          const response = await fetch(endpoint);
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || t('shop.loadFailed'));
          }

          const data = await response.json();

          if (data.products && data.pagination) {
            setProducts(data.products);
            setPagination({
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              totalItems: data.pagination.totalItems,
              itemsPerPage: data.pagination.itemsPerPage,
            });
          } else {
            setProducts(data);
          }
        } catch (fetchError) {
          if (fetchError.response?.status === 404 ||
              fetchError.message?.toLowerCase().includes('not found') ||
              fetchError.message?.toLowerCase().includes('no longer available') ||
              fetchError.message?.toLowerCase().includes('category not found')) {
            setProducts([]);
            setError(null);
          } else {
            const errorMessage = handleApiError(fetchError);
            setError(errorMessage);
          }
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [slug, id, attributeProducts, location.search, sortOption, defaultSortLoaded]);

  // Reset list-tracking when the list identity changes so a new category always fires once.
  useEffect(() => {
    lastTrackedItemList.current = null;
  }, [slug, id, attributeProducts]);

  useEffect(() => {
    if (loading || products.length === 0) {
      return;
    }

    // URL page/limit = user intent. Do not use server-filled pagination state:
    // setPagination after fetch can change itemsPerPage and re-fire GA4 + Meta
    // for the same visual page view (pre-existing list double-count risk).
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page'), 10) || 1;
    const limit = parseInt(searchParams.get('limit'), 10) || 24;
    const trackingKey = buildCategoryListTrackingKey({
      listId: listAnalytics.listId,
      page,
      limit,
      search: location.search,
      sortOption,
    });

    if (!shouldTrackCategoryList(lastTrackedItemList.current, trackingKey)) {
      return;
    }

    trackViewItemList(products, listAnalytics);
    // Explicit Meta category audience signal (not used on search pages).
    trackViewCategory(products, listAnalytics);
    lastTrackedItemList.current = trackingKey;
  }, [listAnalytics, loading, location.search, products, sortOption]);

  const handlePageChange = (newPage) => {
    try {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('page', newPage);
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (pageError) {
      console.error('Error changing page:', pageError);
    }
  };

  const getTitle = (titleJson) => parseMultiLangContent(titleJson, activeLang);

  const categoryTitle = categoryInfo
    ? getTitle(attributeProducts
      ? (categoryInfo.attribute_title || categoryInfo.menu_title)
      : categoryInfo.title)
    : (attributeProducts ? `Attribute ${id}` : slug || t('shop.products'));

  const sortLabel = t(SORT_LABEL_KEYS[sortOption] || SORT_LABEL_KEYS.default);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        p: 4,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        color: 'text.primary',
      }}
      >
        <Typography variant="h6" component="h2">
          {t('product.unavailableTitle')}
        </Typography>
        <Typography variant="body1">
          {error}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('product.unavailableRefresh')}
        </Typography>
      </Box>
    );
  }

  let seoTitle = attributeProducts ? t('shop.products') : t('shop.category');
  let seoDescription = t('shop.seoDescription');
  let canonicalUrl = '';
  let structuredData = null;

  if (categoryInfo) {
    const baseUrl = window.location.origin;
    if (attributeProducts) {
      const attributeTitle = categoryInfo.attribute_title || categoryInfo.menu_title || t('shop.products');
      seoTitle = typeof attributeTitle === 'string' && attributeTitle.startsWith('{')
        ? JSON.parse(attributeTitle).EN || t('shop.products')
        : attributeTitle;
      seoDescription = `Shop our collection of ${seoTitle} products`;
      canonicalUrl = `${baseUrl}/product-category/${id}`;
    } else {
      seoTitle = categoryInfo.title || t('shop.category');
      if (typeof seoTitle === 'string' && seoTitle.startsWith('{')) {
        try {
          seoTitle = JSON.parse(seoTitle).EN || 'Category';
        } catch (e) {
          // Use as is if parsing fails
        }
      }
      seoDescription = categoryInfo.meta?.description || `Shop our collection of ${seoTitle} products`;
      canonicalUrl = `${baseUrl}/category/${slug}`;
      structuredData = categoryInfo.structuredData;
    }
  } else {
    seoTitle = attributeProducts ? `Products by Attribute ID ${id}` : `${slug} Collection`;
    seoDescription = `Shop our collection of ${attributeProducts ? 'products by attribute' : slug} products`;
    canonicalUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    canonicalUrl += attributeProducts ? `/product-category/${id}` : `/category/${slug}`;
  }

  return (
    <>
      <SEO
        meta={{
          title: `${seoTitle} | EladoreRuffles`,
          description: seoDescription,
          canonical: canonicalUrl,
        }}
        structuredData={structuredData}
      />

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Breadcrumb row */}
        <Box sx={{ mb: 2 }}>
          <Typography
            component="nav"
            sx={{
              fontSize: '0.8125rem',
              color: COLORS.textSecondary,
            }}
          >
            <Link
              to="/"
              style={{ color: COLORS.textSecondary, textDecoration: 'none' }}
            >
              Home
            </Link>
            {' / '}
            <span>{categoryTitle}</span>
          </Typography>
        </Box>

        {/* Title row */}
        <Box sx={{ mb: 3 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONTS.heading,
              fontWeight: 600,
              fontSize: '2rem',
              color: COLORS.textPrimary,
              mb: 0.5,
            }}
          >
            {categoryTitle}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: COLORS.textSecondary,
            }}
          >
            {pagination.totalItems} products
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Desktop Sidebar */}
          <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <ProductFilters />
          </Grid>

          <Grid item xs={12} md={9}>
            {/* Mobile segmented Filters / Sort bar */}
            {isMobile && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                border: `1px solid ${COLORS.borderControl}`,
                borderRadius: '2px',
                minHeight: 46,
                mb: 3,
                overflow: 'hidden',
              }}
            >
              <Box
                component="button"
                type="button"
                aria-label={`Filters (${activeFilterCount})`}
                onClick={openFilterSheet}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  border: 'none',
                  borderRight: `1px solid ${COLORS.borderControl}`,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  fontFamily: FONTS.body,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                  p: '10px 12px',
                }}
              >
                <TuneIcon sx={{ fontSize: 18, color: COLORS.green }} />
                {`Filters (${activeFilterCount})`}
              </Box>
              <Box
                component="button"
                type="button"
                aria-label={t('shop.sortPrefix', { label: sortLabel })}
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
                aria-controls={sortOpen ? MOBILE_SORT_LISTBOX_ID : undefined}
                onClick={() => setSortOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  border: 'none',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  fontFamily: FONTS.body,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                  p: '10px 12px',
                }}
              >
                {t('shop.sortPrefix', { label: sortLabel })}
                <ExpandMoreIcon sx={{ fontSize: 18, color: COLORS.textSecondary }} />
              </Box>
            </Box>
            )}

            {/* Applied filter chips + sort row */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, flex: 1 }}>
                {appliedChips.map((chip) => (
                  <Box
                    key={`${chip.attributeId}-${chip.childId}`}
                    data-testid="filter-chip"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: COLORS.surfaceWarm,
                      borderRadius: '999px',
                      p: '6px 14px',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: COLORS.green,
                    }}
                  >
                    <span>{chip.label}</span>
                    <IconButton
                      size="small"
                      aria-label={`Remove ${chip.label} filter`}
                      onClick={() => removeChip(chip.attributeId, chip.childId)}
                      sx={{
                        p: 0,
                        color: COLORS.green,
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {appliedChips.length > 0 && (
                  <Box
                    component="button"
                    type="button"
                    aria-label={t('shop.clearAllFilters')}
                    onClick={handleClearAllChips}
                    sx={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                      color: COLORS.textSecondary,
                      textDecoration: 'underline',
                      p: '6px 4px',
                    }}
                  >
                    {t('shop.clearAll')}
                  </Box>
                )}
              </Box>

              {/* Sort control — visible on desktop; visually hidden on mobile, opened from segmented bar */}
              <Select
                value={sortOption || 'default'}
                onChange={(e) => setSortOption(e.target.value)}
                open={sortOpen}
                onOpen={() => setSortOpen(true)}
                onClose={() => setSortOpen(false)}
                displayEmpty
                renderValue={() => t('shop.sortPrefix', { label: sortLabel })}
                SelectDisplayProps={isMobile ? {
                  tabIndex: -1,
                  'aria-hidden': true,
                } : undefined}
                sx={{
                  minWidth: isMobile ? '1px' : 200,
                  border: isMobile ? 'none' : `1px solid ${COLORS.borderControl}`,
                  borderRadius: '2px',
                  fontSize: '0.8125rem',
                  ...(isMobile ? {
                    position: 'absolute',
                    width: '1px',
                    maxWidth: '1px',
                    height: '1px',
                    opacity: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                  } : {}),
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { p: '9px 14px' },
                }}
                variant="outlined"
                MenuProps={{
                  MenuListProps: {
                    id: MOBILE_SORT_LISTBOX_ID,
                  },
                  PaperProps: {
                    sx: { maxHeight: 300 },
                  },
                }}
              >
                <MenuItem value="default">{t('shop.sortDefault')}</MenuItem>
                <MenuItem value="price_low_high">{t('shop.sortPriceLowHigh')}</MenuItem>
                <MenuItem value="price_high_low">{t('shop.sortPriceHighLow')}</MenuItem>
                <MenuItem value="newest">{t('shop.sortNewest')}</MenuItem>
              </Select>
            </Box>

            {products.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                <Typography variant="h6">{t('shop.noProductsFound')}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('shop.noProductsHint')}
                </Typography>
              </Box>
            ) : (
              <>
                <Grid
                  container
                  columnSpacing="28px"
                  rowSpacing="24px"
                >
                  {products.map((product, productIndex) => (
                    <Grid item xs={6} sm={6} md={4} key={product.product_id}>
                      <StyledProductCard product={product} analyticsList={{ ...listAnalytics, index: productIndex }} />
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                </Box>
              </>
            )}
          </Grid>
        </Grid>

        <FilterBottomSheet
          open={filterSheetOpen}
          onClose={closeFilterSheet}
          filters={filterOptions}
          selectedFilters={selectedFilters}
          onApply={handleApplyFiltersFromSheet}
          resultCount={pagination.totalItems}
        />
      </Container>
    </>
  );
};

export default ProductList;
